import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase-config";

export function renderLanding(): string {
  return `
    <div class="landing-page">
      <!-- Hero Section -->
      <section class="hero">
        <div class="container">
          <h1 class="hero-title">AI Writing Suggestions,<br/>Right in Your Sidebar</h1>
          <p class="hero-subtitle">SideScribe is a Microsoft Word add-in that reviews your documents for clarity, tone, and style using Claude and ChatGPT — with your own API keys, so you pay the AI providers directly at their rates.</p>
          <div class="hero-actions">
            <a href="#get-sidescribe" class="btn btn-primary btn-lg">Get SideScribe</a>
            <a href="#features" class="btn btn-outline btn-lg">Learn More</a>
          </div>
          <p class="hero-note">7-day free trial. Install from Microsoft Marketplace.</p>
        </div>
      </section>

      <!-- Screenshots Carousel -->
      <section class="screenshots">
        <div class="container">
          <h2 class="section-title">See SideScribe in Action</h2>
          <p class="section-subtitle">AI-powered writing feedback right inside Microsoft Word.</p>
          <div class="carousel">
            <button class="carousel-btn carousel-prev" id="carousel-prev" aria-label="Previous slide">&lsaquo;</button>
            <div class="carousel-track-wrapper">
              <div class="carousel-track" id="carousel-track">
                <div class="carousel-slide">
                  <div class="screenshot-placeholder">
                    <div class="screenshot-mockup">
                      <div class="mockup-toolbar">
                        <div class="mockup-dots"><span></span><span></span><span></span></div>
                        <div class="mockup-title">Document.docx - Microsoft Word</div>
                      </div>
                      <div class="mockup-body">
                        <div class="mockup-doc">
                          <div class="mockup-line w80"></div>
                          <div class="mockup-line w100"></div>
                          <div class="mockup-line w60"></div>
                          <div class="mockup-line w90 highlight"></div>
                          <div class="mockup-line w70"></div>
                          <div class="mockup-line w100"></div>
                        </div>
                        <div class="mockup-sidebar">
                          <div class="mockup-sidebar-header">SideScribe</div>
                          <div class="mockup-suggestion">
                            <div class="mockup-diff-del">wordy and unclear phrasing</div>
                            <div class="mockup-diff-add">concise, direct language</div>
                          </div>
                          <div class="mockup-actions"><span class="mockup-accept">Apply</span><span class="mockup-reject">Skip</span></div>
                        </div>
                      </div>
                    </div>
                    <p class="screenshot-caption">Review suggestions in the sidebar with word-level diffs</p>
                  </div>
                </div>
                <div class="carousel-slide">
                  <div class="screenshot-placeholder">
                    <div class="screenshot-mockup">
                      <div class="mockup-toolbar">
                        <div class="mockup-dots"><span></span><span></span><span></span></div>
                        <div class="mockup-title">Report.docx - Microsoft Word</div>
                      </div>
                      <div class="mockup-body">
                        <div class="mockup-doc">
                          <div class="mockup-line w70"></div>
                          <div class="mockup-line w100"></div>
                          <div class="mockup-line w80"></div>
                          <div class="mockup-line w50"></div>
                          <div class="mockup-line w90"></div>
                          <div class="mockup-line w60"></div>
                        </div>
                        <div class="mockup-sidebar">
                          <div class="mockup-sidebar-header">SideScribe</div>
                          <div class="mockup-model-select">
                            <span class="mockup-model active">Claude</span>
                            <span class="mockup-model">ChatGPT</span>
                          </div>
                          <div class="mockup-prompt-box">
                            <div class="mockup-line w80 light"></div>
                            <div class="mockup-line w60 light"></div>
                          </div>
                          <div class="mockup-run-btn">Review Document</div>
                        </div>
                      </div>
                    </div>
                    <p class="screenshot-caption">Choose your AI model and customize review instructions</p>
                  </div>
                </div>
                <div class="carousel-slide">
                  <div class="screenshot-placeholder">
                    <div class="screenshot-mockup">
                      <div class="mockup-toolbar">
                        <div class="mockup-dots"><span></span><span></span><span></span></div>
                        <div class="mockup-title">Proposal.docx - Microsoft Word</div>
                      </div>
                      <div class="mockup-body">
                        <div class="mockup-doc">
                          <div class="mockup-line w100"></div>
                          <div class="mockup-line w80 highlight"></div>
                          <div class="mockup-line w90 highlight"></div>
                          <div class="mockup-line w70"></div>
                          <div class="mockup-line w100"></div>
                          <div class="mockup-line w40"></div>
                        </div>
                        <div class="mockup-sidebar">
                          <div class="mockup-sidebar-header">SideScribe</div>
                          <div class="mockup-suggestion">
                            <div class="mockup-diff-del">passive voice throughout</div>
                            <div class="mockup-diff-add">active, engaging tone</div>
                          </div>
                          <div class="mockup-suggestion">
                            <div class="mockup-diff-del">redundant qualifier</div>
                            <div class="mockup-diff-add">removed for clarity</div>
                          </div>
                          <div class="mockup-batch-actions"><span>Apply All (2)</span></div>
                        </div>
                      </div>
                    </div>
                    <p class="screenshot-caption">Apply multiple suggestions at once with batch actions</p>
                  </div>
                </div>
              </div>
            </div>
            <button class="carousel-btn carousel-next" id="carousel-next" aria-label="Next slide">&rsaquo;</button>
          </div>
          <div class="carousel-dots" id="carousel-dots">
            <button class="carousel-dot active" data-index="0" aria-label="Slide 1"></button>
            <button class="carousel-dot" data-index="1" aria-label="Slide 2"></button>
            <button class="carousel-dot" data-index="2" aria-label="Slide 3"></button>
          </div>
        </div>
      </section>

      <!-- Features Section -->
      <section class="features" id="features">
        <div class="container">
          <h2 class="section-title">Everything You Need to Write Better</h2>
          <div class="features-grid">
            <div class="feature-card">
              <div class="feature-icon">&#9998;</div>
              <h3>Smart Suggestions</h3>
              <p>Get word-level diffs showing exactly what to change and why, right in your Word sidebar.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">&#9881;</div>
              <h3>Bring Your Own API Key</h3>
              <p>Connect your own Anthropic or OpenAI account. SideScribe calls their APIs directly — we never see your usage or mark up the cost. Switch models anytime.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">&#128196;</div>
              <h3>Full Document or Selection</h3>
              <p>Review your entire document or highlight a specific section for targeted feedback.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">&#128736;</div>
              <h3>One-Click Apply</h3>
              <p>Accept, reject, or modify suggestions individually. Apply changes directly to your document.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">&#128274;</div>
              <h3>Your Keys, Your Privacy</h3>
              <p>API keys are stored securely and synced to your account. Your documents never touch our servers.</p>
            </div>
            <div class="feature-card">
              <div class="feature-icon">&#128172;</div>
              <h3>Custom Instructions</h3>
              <p>Tell SideScribe what to focus on: formality, conciseness, technical accuracy, and more.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Get SideScribe Section -->
      <section class="get-sidescribe" id="get-sidescribe">
        <div class="container">
          <h2 class="section-title">Get SideScribe</h2>
          <p class="section-subtitle">Get started in three easy steps.</p>

          <div class="appsource-badge">
            <a href="https://marketplace.microsoft.com" target="_blank" rel="noopener noreferrer">
              <img src="assets/ms-marketplace-badge.svg" alt="Get it from Microsoft" class="appsource-img" />
            </a>
          </div>

          <div class="get-sidescribe-steps">
            <div class="get-step">
              <div class="get-step-number">1</div>
              <h3>Install from Microsoft Marketplace</h3>
              <p>Click the badge above to install SideScribe directly from the Microsoft Marketplace.</p>
            </div>
            <div class="get-step">
              <div class="get-step-number">2</div>
              <h3>Sign In</h3>
              <p>Your account is created automatically with your Microsoft credentials. No extra signup needed.</p>
            </div>
            <div class="get-step">
              <div class="get-step-number">3</div>
              <h3>Free Trial, Then Subscribe</h3>
              <p>Enjoy a 7-day free trial, then choose monthly ($3/mo) or annual ($24/yr) through the marketplace.</p>
            </div>
          </div>

        </div>
      </section>

      <!-- BYOK Callout -->
      <section class="byok-callout">
        <div class="container">
          <div class="byok-callout-inner">
            <div class="byok-callout-icon">&#128273;</div>
            <div class="byok-callout-text">
              <h3>Your API keys. Their rates. No markup.</h3>
              <p>SideScribe connects your Word add-in directly to Anthropic and OpenAI using API keys from your own accounts. You pay those providers directly — we have no visibility into your usage and add zero markup to AI costs. SideScribe is just the add-in: <strong>$3/mo or $24/yr</strong>.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Individual Licensing Section -->
      <section class="individual-licensing" id="pricing">
        <div class="container">
          <h2 class="section-title">Individual Licensing</h2>
          <div class="pricing-cards">
            <div class="pricing-card">
              <h3>Free Trial</h3>
              <div class="pricing-amount">
                <span class="pricing-value">7</span>
                <span class="pricing-period">days</span>
              </div>
              <ul class="pricing-features">
                <li>Full access to all features</li>
                <li>Claude, ChatGPT &amp; Gemini support</li>
                <li>Unlimited reviews</li>
                <li>No credit card required</li>
              </ul>
              <a href="https://marketplace.microsoft.com" target="_blank" rel="noopener noreferrer" class="btn btn-outline btn-lg pricing-btn">Try Free</a>
            </div>
            <div class="pricing-card">
              <h3>Monthly</h3>
              <div class="pricing-amount">
                <span class="pricing-dollar">$</span>
                <span class="pricing-value">3</span>
                <span class="pricing-period">/mo</span>
              </div>
              <ul class="pricing-features">
                <li>Full access to all features</li>
                <li>Claude, ChatGPT &amp; Gemini support</li>
                <li>Unlimited reviews</li>
                <li>Cancel anytime</li>
              </ul>
              <a href="https://marketplace.microsoft.com" target="_blank" rel="noopener noreferrer" class="btn btn-primary btn-lg pricing-btn">Get Started</a>
            </div>
            <div class="pricing-card pricing-card-featured">
              <div class="pricing-badge">Best Value</div>
              <h3>Annual</h3>
              <div class="pricing-amount">
                <span class="pricing-dollar">$</span>
                <span class="pricing-value">24</span>
                <span class="pricing-period">/yr</span>
              </div>
              <p class="pricing-savings">Save 33%</p>
              <ul class="pricing-features">
                <li>Full access to all features</li>
                <li>Claude, ChatGPT &amp; Gemini support</li>
                <li>Unlimited reviews</li>
                <li>Cancel anytime</li>
              </ul>
              <a href="https://marketplace.microsoft.com" target="_blank" rel="noopener noreferrer" class="btn btn-primary btn-lg pricing-btn">Get Started</a>
            </div>
          </div>
          <p class="pricing-note">All plans include a 7-day free trial. Subscriptions managed through the Microsoft Marketplace.</p>
          <p class="pricing-note" style="margin-top: 8px;">AI usage costs are separate — you pay Anthropic or OpenAI directly at their standard API rates. SideScribe does not mark up or meter your AI usage.</p>
        </div>
      </section>

      <!-- Enterprise Licensing Section -->
      <section class="enterprise" id="enterprise">
        <div class="container">
          <h2 class="section-title">Enterprise Licensing</h2>
          <p class="section-subtitle">Deploy SideScribe across your organization with centralized API key management and admin controls — including support for your enterprise's own AI environment.</p>

          <div class="enterprise-benefits">
            <div class="enterprise-benefit">
              <div class="enterprise-benefit-icon">&#128272;</div>
              <div>
                <h4>Centralized API Key Management</h4>
                <p>Provision API keys org-wide from the admin portal. Users get access without handling credentials themselves.</p>
              </div>
            </div>
            <div class="enterprise-benefit">
              <div class="enterprise-benefit-icon">&#127970;</div>
              <div>
                <h4>Your Enterprise AI Environment</h4>
                <p>Connect SideScribe to your organization's private AI deployment — Azure OpenAI, AWS Bedrock, Anthropic on-prem, or other custom endpoints. Keeps data inside your security perimeter and supports proprietary or fine-tuned models.</p>
              </div>
            </div>
            <div class="enterprise-benefit">
              <div class="enterprise-benefit-icon">&#128101;</div>
              <div>
                <h4>Admin Controls</h4>
                <p>Manage licenses, assign seats, and control feature access across your organization from a single admin portal.</p>
              </div>
            </div>
          </div>

          <div class="contact-form-card">
            <h3>Contact Us</h3>
            <p>Interested in enterprise licensing? Fill out the form and we'll get back to you.</p>
            <form id="contact-form">
              <div class="form-row">
                <div class="form-group">
                  <label for="contact-name">Name</label>
                  <input type="text" id="contact-name" placeholder="Your name" required />
                </div>
                <div class="form-group">
                  <label for="contact-email">Email</label>
                  <input type="email" id="contact-email" placeholder="you@company.com" required />
                </div>
              </div>
              <div class="form-group">
                <label for="contact-org">Organization</label>
                <input type="text" id="contact-org" placeholder="Company name" />
              </div>
              <div class="form-group">
                <label for="contact-message">Message</label>
                <textarea id="contact-message" rows="4" placeholder="Tell us about your needs..." required></textarea>
              </div>
              <div id="contact-status" class="form-status" style="display:none;"></div>
              <button type="submit" class="btn btn-primary btn-lg">Send Message</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  `;
}

export function mountLanding(): void {
  // Carousel
  const track = document.getElementById("carousel-track") as HTMLElement | null;
  const dots = document.querySelectorAll<HTMLButtonElement>(".carousel-dot");
  const prevBtn = document.getElementById("carousel-prev");
  const nextBtn = document.getElementById("carousel-next");
  let current = 0;
  const total = dots.length;

  function goTo(index: number) {
    current = ((index % total) + total) % total;
    if (track) track.style.transform = `translateX(-${current * 100}%)`;
    dots.forEach((d, i) => d.classList.toggle("active", i === current));
  }

  prevBtn?.addEventListener("click", () => goTo(current - 1));
  nextBtn?.addEventListener("click", () => goTo(current + 1));
  dots.forEach((dot) => {
    dot.addEventListener("click", () => goTo(Number(dot.dataset.index)));
  });

  // Auto-advance every 5s, pause on hover
  let timer = setInterval(() => goTo(current + 1), 5000);
  const carousel = track?.parentElement?.parentElement;
  carousel?.addEventListener("mouseenter", () => clearInterval(timer));
  carousel?.addEventListener("mouseleave", () => {
    timer = setInterval(() => goTo(current + 1), 5000);
  });

  // Contact form
  const form = document.getElementById("contact-form") as HTMLFormElement | null;
  form?.addEventListener("submit", async (e) => {
    e.preventDefault();

    const name = (document.getElementById("contact-name") as HTMLInputElement).value.trim();
    const email = (document.getElementById("contact-email") as HTMLInputElement).value.trim();
    const organization = (document.getElementById("contact-org") as HTMLInputElement).value.trim();
    const message = (document.getElementById("contact-message") as HTMLTextAreaElement).value.trim();
    const statusEl = document.getElementById("contact-status")!;
    const submitBtn = form.querySelector("button[type=submit]") as HTMLButtonElement;

    if (!name || !email || !message) return;

    submitBtn.disabled = true;
    submitBtn.textContent = "Sending...";

    try {
      await addDoc(collection(db, "contactRequests"), {
        name,
        email,
        organization: organization || null,
        message,
        createdAt: Timestamp.now(),
      });

      statusEl.className = "form-status form-success";
      statusEl.textContent = "Message sent! We'll be in touch soon.";
      statusEl.style.display = "block";
      form.reset();
    } catch (err: any) {
      statusEl.className = "form-status form-error";
      statusEl.textContent = "Failed to send message. Please try again.";
      statusEl.style.display = "block";
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = "Send Message";
    }
  });
}
